import type { Revision, RevisionFile } from '../../types';
import { addUserEdits, type Edits, isYValue, jsonToY, mergeEdits, mutations } from '../../utils';

/**
 * Insert a file in a revision.
 */
export function insertFileInRevision(
    revision: mutations.MutableData<Revision>,
    file: RevisionFile
): void {
    const existingFile = mutations.getIn(revision, ['files', file.id] as const);
    if (existingFile) {
        throw new Error('File with same ID already exists in revision');
    }

    const revisionFiles = mutations.get(revision, 'files');
    mutations.set(revisionFiles, file.id, file);
}

/**
 * Remove a file in a revision.
 */
export function removeFileInRevision(
    revision: mutations.MutableData<Revision>,
    fileId: string
): void {
    mutations.delIn(revision, ['files', fileId] as const);
}

/**
 * Update the content of a file in a revision.
 */
export function updateFileInRevision(
    revision: mutations.MutableData<Revision>,
    fileId: string,
    update: Partial<Omit<RevisionFile, 'id'>>
): void {
    const existingFile = mutations.getIn(revision, ['files', fileId] as const);
    if (!existingFile) {
        throw new Error(`File doesn't exist in revision`);
    }

    Object.entries(update).forEach(([key, value]) => {
        if (
            key === 'downloadURL' &&
            value !== mutations.get(existingFile, 'downloadURL') &&
            !('blobId' in update)
        ) {
            // If the download URL is changed, we need to remove the blob ID
            mutations.del(existingFile, 'blobId');
        }

        mutations.set(
            existingFile,
            key as keyof RevisionFile,
            // @ts-ignore
            isYValue(revision) ? jsonToY(value) : value
        );
    });
}

/**
 * Mark a file as edited by a user.
 */
export function markFileAsEdited(
    revision: mutations.MutableData<Revision>,
    fileId: string,
    userId: string | string[] | mutations.MutableData<Edits>
) {
    const yEdits = mutations.getIn(revision, ['files', fileId, 'edits'] as const);
    if (!yEdits) {
        return;
    }

    const result = (() => {
        if (Array.isArray(userId)) {
            return addUserEdits(yEdits, userId);
        }
        if (typeof userId === 'string') {
            return addUserEdits(yEdits, [userId]);
        }
        return mergeEdits(yEdits, userId);
    })();
    mutations.setIn(revision, ['files', fileId, 'edits'] as const, result);
}
